import React, { useMemo, useCallback, useRef, useState, FocusEvent, MouseEvent } from 'react';
import { useRouter } from 'next/navigation';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { Color } from '@diritto/common/style/color';
import { numberComma } from '@diritto/common/utils/string';
import { ReplyErrorCodes } from '@diritto/common/reply/constants';
import { useAuthState } from 'contexts/AuthProvider';
import { Button } from 'ui-components/common/Button';
import { ButtonSize, ButtonType } from '@/constants/button';
import { ProfileImage } from '@/app-components/accounts/profiles/ProfileImage';
import { SignLinkButton } from '@/ui-components/common/SignLinkButton';
import { showToast } from 'utils/modal';
import { ModalHandler } from '@/utils/ModalHandler';
import { ModalType } from '@/constants/modal';

interface PromotionReplyInputViewProps {
  isAnswered: boolean;
  onSend: (value: string, callback?: (success?: boolean) => void) => void;
  maxLength: number;
}

export const PromotionReplyInputView = ({ isAnswered, onSend, maxLength }: PromotionReplyInputViewProps) => {
  const router = useRouter();
  const { profile } = useAuthState();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [isFocused, setIsFocused] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const handleInvalidPromotionModal = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.blur();
    }

    ModalHandler.show(ModalType.Alert, {
      title: '댓글 이벤트는 밸런스 게임 선택 완료 후 참여할 수 있어요.',
      confirmText: '선택하러 가기',
      cancelText: '닫기',
      onConfirm: () => router.push(`#my`),
    });
  }, [router]);

  const handleEditing = useCallback(() => {
    const value = textareaRef?.current?.value ?? '';
    if (value.length >= maxLength) {
      showToast(`댓글은 ${numberComma(maxLength)}자 이하여야 해요.`);
    }

    if (value.trim().length > 0) {
      setIsEditing(true);
    } else {
      setIsEditing(false);
    }
  }, [maxLength]);

  const handleChange = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) {
      return;
    }

    const lineHeight = 21;
    const padding = 4;

    ta.style.height = 'auto';
    const lines = Math.min(3, Math.ceil(ta.scrollHeight / (lineHeight + padding)));
    const newHeight = lines * lineHeight + padding;
    ta.style.height = `${newHeight}px`;

    handleEditing();
  }, [handleEditing]);

  const handleFocus = useCallback(
    (e: FocusEvent<HTMLTextAreaElement>) => {
      if (!isAnswered) {
        handleInvalidPromotionModal();
        return;
      }

      setIsFocused(true);
      e.target.selectionStart = e.target.value.length;
      e.target.scrollTop = e.target.scrollHeight;
    },
    [isAnswered, handleInvalidPromotionModal],
  );

  const handleClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      const value = textareaRef?.current?.value ?? '';
      if (value.trim().length > maxLength) {
        showToast(`댓글은 ${numberComma(maxLength)}자 이하여야 해요.`);
        return;
      }

      if (value.trim().length > 0) {
        onSend?.(value, (success?: boolean, errorCode?: string) => {
          const ta = textareaRef.current;
          if (ta) {
            ta.style.height = `25px`;
          }

          if (textareaRef?.current) {
            textareaRef.current.value = '';
          }
          e?.currentTarget?.blur();
          setIsEditing(false);

          if (success) {
            showToast('댓글 등록이 완료되었어요.');
          } else if (errorCode === ReplyErrorCodes.RestrictWriteReplyInterval) {
            showToast('댓글 작성 이후 1분 내에 새 댓글을 작성할 수 없어요.');
          } else {
            showToast('댓글 등록에 실패했어요.');
          }
        });
      }
    },
    [onSend, textareaRef, maxLength],
  );

  const handleShortcut = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.shiftKey && e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        handleClick(e as any);
        textareaRef?.current?.blur();
      }
    },
    [handleClick, textareaRef],
  );

  const placeholder = useMemo(() => {
    if (!profile) {
      return '댓글을 작성하려면 로그인해주세요.';
    }

    return '댓글을 작성해주세요.';
  }, [profile]);

  if (!profile) {
    return (
      <StyledContainer className="p-4 mb-[23px] outline outline-1 outline-offset-[-1px] outline-gray-300">
        <StyledLoginText>{placeholder}</StyledLoginText>
        <SignLinkButton label="로그인" size={ButtonSize.Small} buttonTypography={Typography.XXXSmall} />
      </StyledContainer>
    );
  }

  return (
    <StyledContainer
      className={`py-4 px-[10px] mb-[23px] outline outline-1 outline-offset-[-1px] ${isFocused ? 'outline-primary-500' : 'outline-gray-300'}`}>
      <StyledProfile className="h-full flex items-center justify-center">
        <ProfileImage file={profile.profileImage} defaultImageKey={profile.nickname} size={32} />
      </StyledProfile>
      <div className="flex w-full h-full items-center justify-center">
        <StyledTextArea
          ref={textareaRef}
          placeholder={placeholder}
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyUp={handleShortcut}
          onBlur={() => setIsFocused(false)}
          rows={1}
          maxLength={maxLength}
        />
      </div>
      <StyledButton
        label="등록"
        buttonType={ButtonType.Primary}
        buttonTypography={Typography.XXXSmall}
        bgColor={isEditing ? Color.Primary500 : Color.Gray300}
        size={ButtonSize.Small}
        onClick={handleClick}
      />
    </StyledContainer>
  );
};

const StyledContainer = styled.section`
  width: 100%;
  min-height: 72px;
  height: auto;

  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 10px;
  gap: 8px;

  border-radius: 4px;
  background-color: ${Color.White};
`;

const StyledLoginText = styled.p`
  color: ${Color.Gray500};
  font-weight: ${FontWeight.Medium};
  ${Typography.XXSmall};
`;

const StyledProfile = styled.div`
  flex-shrink: 0;

  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledButton = styled(Button)(
  ({ bgColor, color = Color.White }: { bgColor?: string; color?: string }) => css`
    flex-basis: content;
    color: ${color};
    background-color: ${bgColor};
    border: none;
    ${Typography.XXXSmall};
  `,
);

const StyledTextArea = styled.textarea`
  display: flex;
  align-items: center;
  border: none;
  resize: none;
  overflow: hidden;
  font-family: inherit;
  width: 100%;
  display: block;
  appearance: none;

  line-height: 21px;
  color: ${Color.Gray900};
  ${Typography.XXSmall};
  font-weight: ${FontWeight.Medium};

  &::placeholder {
    color: ${Color.Gray500};
  }

  &:focus {
    outline: none;
  }
`;
